import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';

import TrustSignals from './components/TrustSignals';
import { useAuth } from '../../contexts/AuthContext';

import { supabase } from '../../lib/supabase';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, signOut, isAuthenticated, tenantMembership, isGymOwner } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', tenantId: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const { data, error } = await supabase?.from('tenants')?.select('id, name, address')?.eq('status', 'active')?.order('name');
      
      if (error) throw error;
      setTenants(data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setTenantsLoading(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && tenantMembership) {
      if (isGymOwner) {
        navigate('/admin-dashboard');
      } else {
        navigate('/member-dashboard');
      }
    }
  }, [isAuthenticated, tenantMembership, isGymOwner, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'Email or username is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email) && !/^[a-zA-Z0-9_]{3,}$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address or username';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, rememberMe: checked }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        setErrors({
          email: error?.message,
          password: error?.message
        });
        return;
      }
      
      // Navigation handled by useEffect after auth state updates
    } catch (err) {
      setErrors({
        email: 'An unexpected error occurred',
        password: 'An unexpected error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/images/pexels-eran-design-2158190390-35215421-1769216120583.jpg)',
          filter: 'brightness(0.4)'
        }}
      />
      
      {/* Content Overlay */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-8 md:py-12 lg:py-16">
        <div className="w-full max-w-md lg:max-w-lg">
          <div className="text-center mb-6 md:mb-8 lg:mb-10">
            <div className="flex justify-center mb-4 md:mb-6">
              <div className="flex h-16 w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 items-center justify-center rounded-2xl bg-primary/10 shadow-lg">
                <img 
                  src="/assets/images/gym_logo_fitness_wellness.png" 
                  alt="Gym fitness wellness logo with stylized dumbbell design"
                  className="w-full h-full object-contain p-2"
                />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2 md:mb-3">
              Welcome Back
            </h1>
            <p className="text-base md:text-lg text-white">
              Sign in to continue your fitness journey
            </p>
          </div>

          <div className="bg-card rounded-2xl shadow-xl p-6 md:p-8 lg:p-10 mb-6">
            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address or Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="Mail" size={20} color="var(--color-muted-foreground)" />
                  </div>
                  <Input
                    id="email"
                    name="email"
                    type="text"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com or username"
                    className={`pl-12 ${
                      errors?.email ? 'border-destructive focus:ring-destructive' : ''
                    }`}
                    disabled={isLoading}
                  />
                </div>
                {errors?.email && (
                  <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                    <Icon name="AlertCircle" size={16} />
                    {errors?.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="Lock" size={20} color="var(--color-muted-foreground)" />
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData?.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className={`pl-12 pr-12 ${
                      errors?.password ? 'border-destructive focus:ring-destructive' : ''
                    }`}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    disabled={isLoading}
                  >
                    <Icon 
                      name={showPassword ? 'EyeOff' : 'Eye'} 
                      size={20} 
                      color="var(--color-muted-foreground)" 
                    />
                  </button>
                </div>
                {errors?.password && (
                  <p className="mt-2 text-sm text-destructive flex items-center gap-1">
                    <Icon name="AlertCircle" size={16} />
                    {errors?.password}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rememberMe"
                    checked={formData?.rememberMe}
                    onCheckedChange={handleCheckboxChange}
                    disabled={isLoading}
                  />
                  <label htmlFor="rememberMe" className="text-sm text-foreground cursor-pointer">
                    Remember me
                  </label>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="LogIn" size={20} />
                    <span>Sign In</span>
                  </div>
                )}
              </Button>
            </form>
          </div>

          <div className="text-center">
            <p className="text-sm text-white">
              Do not have an account?{' '}
              <Link
                to="/register"
                className="font-semibold text-primary hover:text-primary/80 transition-smooth"
              >
                Sign up for free
              </Link>
            </p>
          </div>

          <TrustSignals />
        </div>
      </div>
    </div>
  );
};

export default Login;